import { useForm } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormControl } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCreateFollowUp } from '@/hooks/useStakeholderInteractions';
import { useLocalStorageManager } from '@/hooks/useLocalStorageManager';
import type { CRMStakeholder } from '@/lib/crm/types';
import { toast } from 'sonner';

interface AddFollowUpDialogProps {
  stakeholders: CRMStakeholder[];
  preselectedStakeholder?: CRMStakeholder | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  prefilledDate?: Date;
}

interface FormData {
  stakeholder_id: string;
  date: string;
  type: string;
  subject: string;
  outcome: string;
  attendees: string;
  notes: string;
}

export function AddFollowUpDialog({ 
  stakeholders,
  preselectedStakeholder,
  isOpen, 
  onClose, 
  onSuccess, 
  prefilledDate 
}: AddFollowUpDialogProps) {
  const { mutateAsync: createFollowUp, isPending } = useCreateFollowUp();
  const { getStandardValue, setStandardValue, removeStandardValue } = useLocalStorageManager();
  
  const draftKey = `add-followup-draft-${preselectedStakeholder?.id || 'global'}`;
  const hasLoadedDraftRef = useRef(false);
  
  const form = useForm<FormData>({
    defaultValues: {
      stakeholder_id: preselectedStakeholder?.id || '',
      date: prefilledDate ? format(prefilledDate, "yyyy-MM-dd'T'HH:mm") : '',
      type: 'Follow-up',
      subject: '',
      outcome: '',
      attendees: '',
      notes: ''
    }
  });
  
  // Load draft on mount
  useEffect(() => {
    if (isOpen && !hasLoadedDraftRef.current) {
      const draft = getStandardValue<FormData>(draftKey, null as any);
      if (draft) {
        form.reset({
          stakeholder_id: preselectedStakeholder?.id || draft.stakeholder_id,
          date: prefilledDate ? format(prefilledDate, "yyyy-MM-dd'T'HH:mm") : draft.date,
          type: draft.type,
          subject: draft.subject,
          outcome: draft.outcome,
          attendees: draft.attendees,
          notes: draft.notes
        });
      } else {
        if (preselectedStakeholder) {
          form.setValue('stakeholder_id', preselectedStakeholder.id);
        }
        if (prefilledDate) {
          form.setValue('date', format(prefilledDate, "yyyy-MM-dd'T'HH:mm"));
        }
      }
      hasLoadedDraftRef.current = true;
    }
    
    // Reset flag when dialog closes
    if (!isOpen) {
      hasLoadedDraftRef.current = false;
    }
  }, [isOpen, draftKey, getStandardValue, preselectedStakeholder, prefilledDate, form]);
  
  // Auto-save draft
  useEffect(() => {
    if (!isOpen) return;
    const subscription = form.watch((values) => {
      setStandardValue(draftKey, values as FormData);
    });
    return () => subscription.unsubscribe();
  }, [isOpen, form, draftKey, setStandardValue]);
  
  const onSubmit = async (data: FormData) => {
    if (!data.stakeholder_id) {
      toast.error('Vui lòng chọn stakeholder');
      return;
    }
    if (!data.date) {
      toast.error('Vui lòng chọn ngày follow-up');
      return;
    }
    
    await createFollowUp({
      stakeholderId: data.stakeholder_id,
      date: new Date(data.date).toISOString(),
      type: data.type,
      subject: data.subject,
      outcome: data.outcome,
      attendees: data.attendees,
      notes: data.notes
    });
    
    removeStandardValue(draftKey);
    onSuccess();
    onClose();
    form.reset();
  };
  
  const handleClose = () => {
    removeStandardValue(draftKey);
    form.reset();
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Thêm Follow-up</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              name="stakeholder_id"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Stakeholder *</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Chọn stakeholder..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="max-h-[300px]">
                      {stakeholders.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} {s.role && `- ${s.role}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            
            <FormField
              name="type"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Loại</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Chọn loại..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Follow-up">Follow-up</SelectItem>
                      <SelectItem value="Meeting">Meeting</SelectItem>
                      <SelectItem value="Call">Call</SelectItem>
                      <SelectItem value="Site Visit">Site Visit</SelectItem>
                      <SelectItem value="Delivery">Delivery</SelectItem>
                      <SelectItem value="Approval">Approval</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            
            <FormField
              name="date"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ngày & Giờ Follow-up *</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="subject"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Chủ đề *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nhập chủ đề..." />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="outcome"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kết quả</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Kết quả mong đợi..." rows={2} />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="attendees"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Người tham dự</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nguyễn Văn A, Trần Thị B..." />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="notes"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ghi chú</FormLabel>
                  <FormControl>
                    <Textarea 
                      {...field} 
                      placeholder="Ghi chú bổ sung..." 
                      rows={2}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
                Hủy
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Đang lưu...' : 'Lưu Follow-up'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
